import PayLaterReminder from "../components/PayLaterReminder";
import ExitQuoteCapture from "../components/ExitQuoteCapture";
import FloatingBookingShortcut from "../components/FloatingBookingShortcut";
import { useEffect, useState } from "react";
import { useParams, Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useLenis } from "lenis/react";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Leaf,
  Eye,
  Wind,
  Thermometer,
  Filter,
  Fan,
  Zap,
  Wifi,
  ShieldCheck,
  Droplets,
  MoveHorizontal,
  Minimize2,
  Palette,
  ArrowDown,
  LayoutGrid,
  Sparkles,
  Star,
  Phone,
} from "lucide-react";
import { PageHero, GoogleRating } from "../components/sections";
import Reveal from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import DaikinIntroEditorial from "../components/DaikinIntroEditorial";
import { SPLIT_BRANDS, FEATURED_REVIEW, PHONE_TEL } from "../lib/data";
import { DAIKIN_COMPACT_FEATURES, DAIKIN_STREAMER_FOOTNOTE } from "../lib/daikinCompactFeatures";

const RINNAI_LOCAL_HERO_IMAGE = `${process.env.PUBLIC_URL || ""}/landing/overheated-bulldog-hero.webp`;
const SOCIAL_PROOF_DOG_IMAGE = `${process.env.PUBLIC_URL || ""}/landing/rinnai-google-dog-transparent.webp`;
// Local offer hero artwork. Mobile uses the bright image-first layout.

const DAIKIN_ICON_MAP = {
  leaf: Leaf,
  eye: Eye,
  wind: Wind,
  thermometer: Thermometer,
  filter: Filter,
  fan: Fan,
  zap: Zap,
  wifi: Wifi,
  shield: ShieldCheck,
  droplets: Droplets,
  arrows: MoveHorizontal,
  minimize: Minimize2,
  palette: Palette,
  arrowdown: ArrowDown,
  grid: LayoutGrid,
};


// Official manufacturer media used where the public manufacturer site exposes a
// stable direct asset URL. Existing local product renders remain the fallback.
const OFFICIAL_PRODUCT_IMAGES = {
  "electric-ap": "https://www.mitsubishielectric.com.au/wp-content/uploads/2022/02/18OCT_MTBS_AP_AUS_image_03_0079_m-1920x1440-1-1200x900.png",
  "heavy-ciara": "https://www.mhiaa.com.au/wp-content/uploads/2024/01/MHIAA_Ciara_WebHeroImage_588x330px_06.26-1.jpg",
};

const NON_DAIKIN_FEATURES = {
  "rinnai-local": [
    { title: "7-Year Warranty", desc: "Seven years of warranty cover on this Rinnai installed special for extra peace of mind.", fallback: ShieldCheck, highlight: true },
    { title: "2-Day Installation Guarantee", desc: "Eligible standard installations booked from this offer are installed within 2 days.", fallback: Zap },
    { title: "Wi-Fi Control", desc: "Smart control is available on the Rinnai system used for this offer.", fallback: Wifi },
    { title: "No More To Pay*", desc: "The advertised price is the installed price for qualifying standard installations.", fallback: Check },
  ],
  "daikin-lite-local": [
    { title: "5-Year Warranty", desc: "Daikin manufacturer warranty for long-term peace of mind.", fallback: ShieldCheck },
    { title: "2-Day Installation Guarantee", desc: "Eligible standard installations booked from this offer are installed within 2 days.", fallback: Zap },
    { title: "Blue Fin Anti-Corrosive Coating", desc: "Added outdoor heat-exchanger protection suited to coastal environments.", fallback: ShieldCheck },
    { title: "No More To Pay*", desc: "The advertised price is the installed price for qualifying standard installations.", fallback: Check },
  ],
  "pb-series": [
    {
      title: "Wi-Fi Control",
      desc: "Control the system remotely with Rinnai's supported NetHome Plus app.",
      icon: "https://www.rinnai.com.au/wp-content/uploads/ICO-logo-wifi-IMA.png",
      fallback: Wifi,
    },
    { title: "Quiet Operation", desc: "Low-noise operation designed to suit bedrooms and living spaces.", fallback: Fan },
    { title: "3D Airflow", desc: "Horizontal and vertical swing helps distribute air more evenly around the room.", fallback: Wind },
    { title: "Dehumidifying", desc: "Dry mode helps manage room humidity during hot, humid weather.", fallback: Droplets },
  ],
  "px-series": [
    {
      title: "Wi-Fi + Voice",
      desc: "App control plus Google Home and Amazon Alexa compatibility.",
      icon: "https://www.rinnai.com.au/wp-content/uploads/ICO-logo-wifi-IMA.png",
      fallback: Wifi,
    },
    { title: "Human Sensor", desc: "Detects when the room is empty and can reduce unnecessary energy use.", fallback: Eye },
    { title: "3D Airflow", desc: "Horizontal and vertical swing helps balance room temperature and comfort.", fallback: Wind },
    { title: "Humidity Control", desc: "Set and manage room humidity through Dry Mode on supported PX systems.", fallback: Droplets },
  ],
  "electric-ap": [
    {
      title: "Quiet Operation",
      desc: "Very low indoor sound levels make AP a strong choice for bedrooms and quiet spaces.",
      icon: "https://www.mitsubishielectric.com.au/wp-content/uploads/2025/08/quiet_operation.svg",
      fallback: Fan,
    },
    {
      title: "Night Mode",
      desc: "Reduces operating sound and dims indicator brightness for more comfortable night use.",
      icon: "https://www.mitsubishielectric.com.au/wp-content/uploads/2025/08/night_mode.svg",
      fallback: Star,
    },
    {
      title: "Built-In Wi-Fi",
      desc: "Compatible models include Wi-Fi control for remote operation and scheduling.",
      icon: "https://www.mitsubishielectric.com.au/wp-content/uploads/2025/07/built-in-wi-fi-control.svg",
      fallback: Wifi,
    },
    {
      title: "Dual Barrier Coating",
      desc: "A coating on key internal parts helps reduce dust and greasy dirt build-up.",
      icon: "https://www.mitsubishielectric.com.au/wp-content/uploads/2025/08/dual-barrier-coating-v1.svg",
      fallback: ShieldCheck,
    },
  ],
  "heavy-ciara": [
    {
      title: "Built-In Wi-Fi",
      desc: "Control Ciara from the supported app, with compatible voice-control options.",
      icon: "https://www.mhiaa.com.au/wp-content/uploads/2024/02/Feature-Icons_Built-in_Wi-Fi.svg",
      fallback: Wifi,
    },
    {
      title: "Allergen Clear Filter",
      desc: "MHI's filtration system is designed to capture and manage airborne contaminants on the filter.",
      icon: "https://www.mhiaa.com.au/wp-content/uploads/2024/02/Feature-Icons_RAC-Allergen-Clear-Filter.svg",
      fallback: Filter,
    },
    {
      title: "3D Auto Airflow",
      desc: "Automatically combines vertical and horizontal airflow for wider room coverage.",
      icon: "https://www.mhiaa.com.au/wp-content/uploads/2024/02/Feature-Icons_RAC-3d-Auto.svg",
      fallback: Wind,
    },
    {
      title: "Silent Operation",
      desc: "A dedicated quiet setting reduces sound for bedrooms and low-noise spaces.",
      icon: "https://www.mhiaa.com.au/wp-content/uploads/2024/02/Feature-Icons_Silent-Operation.svg",
      fallback: Fan,
    },
  ],
  "lifestyle-kmtc": [
    { title: "Human Sensor", desc: "Detects movement and can reduce output when the room is unoccupied.", fallback: Eye },
    { title: "Economy Mode", desc: "Limits peak power demand when full output is not required.", fallback: Leaf },
    { title: "Super Quiet", desc: "Reduces indoor fan speed for quieter operation in bedrooms and living areas.", fallback: Fan },
    { title: "Powerful Mode", desc: "Temporarily boosts output to bring the room toward set temperature faster.", fallback: Zap },
    { title: "Apple-Catechin Filter", desc: "Helps capture fine dust and microorganisms on the treated filter surface.", fallback: Filter },
    { title: "Blue Fin", desc: "A corrosion-resistant treatment helps protect the outdoor heat exchanger.", fallback: ShieldCheck },
  ],
  "geo-windfree": [
    { title: "WindFree Cooling", desc: "Maintains comfort by dispersing cool air through thousands of micro air holes.", fallback: Wind },
    { title: "AI Auto Cooling", desc: "Uses room conditions and usage patterns to help select a suitable operating mode.", fallback: Sparkles },
    { title: "SmartThings Wi-Fi", desc: "Built-in Wi-Fi connects the system to Samsung SmartThings for remote control.", fallback: Wifi },
    { title: "Quad-Care Filter", desc: "A multi-stage filter designed to capture fine airborne particles on the filter.", fallback: Filter },
    { title: "Freeze Wash", desc: "Freezes and defrosts the heat exchanger, then dries it as an automated cleaning cycle.", fallback: Droplets },
    { title: "Good Sleep", desc: "Adjusts temperature and airflow overnight with gentler WindFree operation.", fallback: Star },
  ],
};

const RINNAI_LOCAL_SALE_FACTOR = 1750 / 1990;
const RINNAI_LOCAL_DISCOUNT_LABEL = "12% OFF";

const getRinnaiLocalSalePrice = (price) => {
  const regular = Number(String(price).replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(regular)) return price;
  const sale = Math.round(regular * RINNAI_LOCAL_SALE_FACTOR);
  return `$${sale.toLocaleString("en-AU")}`;
};

export const RINNAI_LOCAL_OFFER_RANGES = [
  {
    slug: "rinnai-local",
    manufacturer: "Rinnai",
    name: "Rinnai Split Systems",
    displayName: "Rinnai Split Systems",
    tabLabel: "Rinnai",
    localBrand: "Rinnai",
    blurb: "Rinnai supplied-and-installed special pricing available Sydney-wide, plus the Central Coast and Wollongong. Choose the capacity that suits your room and book while installation spots are available.",
    image: SPLIT_BRANDS.find((b) => b.slug === "rinnai")?.ranges?.[0]?.image,
    prices: [
      { kw: "2.5kW", price: "$1,700", localOfferPrice: "$1,700" },
      { kw: "3.5kW", price: "$1,850", localOfferPrice: "$1,850" },
      { kw: "5.0kW", price: "$2,300", localOfferPrice: "$2,300" },
      { kw: "7.0kW", price: "$2,600", localOfferPrice: "$2,600" },
      { kw: "8.0kW", price: "$3,000", localOfferPrice: "$3,000" },
    ],
  },
  {
    slug: "daikin-lite-local",
    manufacturer: "Daikin",
    name: "Cora",
    displayName: "Daikin Cora",
    tabLabel: "Daikin Cora",
    localBrand: "Daikin",
    blurb: "Daikin Cora supplied and installed at a clear special price Sydney-wide, plus the Central Coast and Wollongong, with a 2-day installation guarantee, 5-year warranty and Blue Fin anti-corrosive coating for added protection in coastal areas.",
    image: SPLIT_BRANDS.find((b) => b.slug === "daikin")?.ranges?.find((r) => r.slug === "cora")?.image,
    prices: [
      { kw: "2.5kW", price: "$2,000", localOfferPrice: "$2,000" },
      { kw: "3.5kW", price: "$2,200", localOfferPrice: "$2,200" },
      { kw: "5.0kW", price: "$2,650", localOfferPrice: "$2,650" },
      { kw: "7.0kW", price: "$3,200", localOfferPrice: "$3,200" },
      { kw: "8.0kW", price: "$3,650", localOfferPrice: "$3,650" },
      { kw: "9.0kW", price: "$4,100", localOfferPrice: "$4,100" },
    ],
  },
];

const BrandSelectionBanner = ({ currentSlug }) => (
  <nav aria-label="Choose split system brand" data-testid="brand-selection-banner" className="border-b border-[#E5E5EA] bg-white">
    <div className="sp-container">
      <div className="-mx-6 flex snap-x snap-mandatory items-center gap-3 overflow-x-auto whitespace-nowrap px-6 py-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0">
        <span className="mr-1 shrink-0 text-[10px] font-bold uppercase tracking-[0.18em] text-[#6E6E73]">Choose brand</span>
        {SPLIT_BRANDS.map((item) => {
          const active = item.slug === currentSlug;
          return active ? (
            <span key={item.slug} aria-current="page" className="snap-start shrink-0 rounded-full border border-[#C8A46A] bg-[#F3E9D2] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-[#0B0B0B]">
              {item.brand}
            </span>
          ) : (
            <Link key={item.slug} to={`/split-systems/${item.slug}`} data-testid={`brand-banner-${item.slug}`} className="group snap-start flex shrink-0 items-center gap-2 rounded-full border border-[#E5E5EA] bg-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.08em] text-[#0B0B0B] transition-all hover:border-[#C8A46A] hover:-translate-y-[1px]">
              {item.brand}<ArrowUpRight className="h-3.5 w-3.5 text-[#C8A46A]" />
            </Link>
          );
        })}
      </div>
    </div>
  </nav>
);

const BrandPage = ({ offerMode = null }) => {
  const { slug } = useParams();
  const isRinnaiLocalOffer = offerMode === "rinnai-local";
  const brand = SPLIT_BRANDS.find((b) => b.slug === (isRinnaiLocalOffer ? "rinnai" : slug));
  const displayRanges = isRinnaiLocalOffer ? RINNAI_LOCAL_OFFER_RANGES : brand?.ranges || [];
  const lenis = useLenis();
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const [activeOfferSlug, setActiveOfferSlug] = useState("rinnai-local");
  const [offerStage, setOfferStage] = useState(1);
  const activeOfferRange = isRinnaiLocalOffer
    ? (displayRanges.find((range) => range.slug === activeOfferSlug) || displayRanges[0])
    : null;

  // Handle direct links from the Deals hub, including navigation between Rinnai and Daikin Cora.
  useEffect(() => {
    if (!isRinnaiLocalOffer || !location.hash) return undefined;
    const targetId = location.hash.replace(/^#/, "");
    const prefix = "range-";
    const rangeSlug = targetId.startsWith(prefix) ? targetId.slice(prefix.length) : null;
    if (rangeSlug && displayRanges.some((range) => range.slug === rangeSlug)) {
      setActiveOfferSlug(rangeSlug);
    }

    const timer = window.setTimeout(() => {
      const el = document.getElementById(targetId);
      if (!el) return;
      if (lenis) lenis.scrollTo(el, { offset: -90, immediate: true });
      else el.scrollIntoView({ behavior: "auto", block: "start" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [isRinnaiLocalOffer, location.hash, lenis]);

  if (!brand) return <Navigate to="/split-systems" replace />;

  const handleBook = (range, priceRow) => {
    const effectivePrice = isRinnaiLocalOffer ? (priceRow.localOfferPrice || getRinnaiLocalSalePrice(priceRow.price)) : priceRow.price;

    if (isRinnaiLocalOffer) {
      setSelected({
        productKey: range.slug,
        displayName: range.displayName || range.name,
        kw: priceRow.kw,
        price: effectivePrice,
      });
      window.setTimeout(() => {
        const el = document.getElementById("deals-select-day") || document.getElementById("local-deal-booking");
        if (!el) return;
        if (lenis) lenis.scrollTo(el, { offset: -95 });
        else el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
      return;
    }

    const sel = { rangeName: range.name, displayName: range.displayName || `${brand.brand} ${range.name}`, localBrand: range.localBrand || brand.brand, kw: priceRow.kw, price: effectivePrice, regularPrice: priceRow.price };
    setSelected(sel);
    setTimeout(() => {
      const el = document.getElementById("book");
      if (!el) return;
      if (lenis) lenis.scrollTo(el, { offset: -20 });
      else el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const jumpToRange = (rangeSlug) => {
    const el = document.getElementById(`range-${rangeSlug}`);
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -20 });
    else el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const selectionMessage = selected
    ? isRinnaiLocalOffer
      ? `I'd like to book the ${selected.localBrand || "Rinnai"} installed special for ${selected.displayName} ${selected.kw} — ${selected.price} supplied & installed on the advertised standard installation terms.`
      : `I'd like to book installation for ${selected.displayName} ${selected.kw} — advertised at ${selected.price} supplied & installed.`
    : isRinnaiLocalOffer
      ? "I'd like to book one of the advertised Rinnai or Daikin Cora supplied-and-installed specials for my area."
      : "";

  const formKey = selected ? `${brand.slug}-${selected.displayName}-${selected.kw}` : `${brand.slug}-default`;
  const submitLabel = selected
    ? isRinnaiLocalOffer
      ? `Claim ${selected.displayName} ${selected.kw} Offer`
      : `Book ${selected.displayName} ${selected.kw}`
    : isRinnaiLocalOffer
      ? "Book My Installation"
      : `Book ${brand.brand} Installation`;

  const pageTitle = isRinnaiLocalOffer
    ? "Rinnai & Daikin Cora Installed Specials | Sydney-Wide, Central Coast & Wollongong | SplitsPro"
    : brand.metaTitle;
  const pageDescription = isRinnaiLocalOffer
    ? "Rinnai and Daikin Cora split-system specials available Sydney-wide, plus the Central Coast and Wollongong, with supplied-and-installed pricing, a 2-day installation guarantee and clear standard-install conditions."
    : brand.metaDesc;
  const canonicalUrl = isRinnaiLocalOffer
    ? "https://splitspro.com.au/split-systems/rinnai-local-offer"
    : `https://splitspro.com.au/split-systems/${brand.slug}`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={brand.image} />
      </Helmet>

      {isRinnaiLocalOffer && (
        <div
          aria-hidden="true"
          data-testid="local-offer-mobile-header-clearance"
          className="lg:hidden"
          style={{ height: "max(0px, calc(var(--sp-site-header-height, 184px) - 36px))" }}
        />
      )}
      <PageHero
        overline={isRinnaiLocalOffer ? "Rinnai & Daikin Cora — Fast Installation Available" : brand.brand}
        title={isRinnaiLocalOffer ? "A hassle-free way to install split systems" : brand.h1}
        sub={isRinnaiLocalOffer ? "Reliable installation. Minimal disruption. 5-star rated. Installations within 2 days. Sydney, Central Coast & Wollongong." : brand.tagline}
        image={isRinnaiLocalOffer ? RINNAI_LOCAL_HERO_IMAGE : brand.image}
        mobileImageFirst={isRinnaiLocalOffer}
        mobileImgPos={isRinnaiLocalOffer ? "object-[62%_center]" : "object-center"}
        desktopBrand
      />









      {!isRinnaiLocalOffer && (
        <>
        <section className="border-b border-[#E8E6E1] bg-[#FBFAF8] py-5 sm:py-6" data-testid="brand-top-proof">
          <div className="sp-container">
            <div className="grid gap-5 lg:grid-cols-[auto_1fr_auto] lg:items-center lg:gap-8">
              <div>
                <GoogleRating />
                <p className="mt-2 text-[11px] font-medium text-[#6E6E73]">Verified customer feedback from SplitsPro&apos;s Google Business Profile.</p>
              </div>
              <div className="min-w-0 border-[#E5E5EA] lg:border-x lg:px-8">
                <div className="flex items-center gap-1">
                  {Array.from({ length: FEATURED_REVIEW.rating || 5 }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-[#FBBC04] text-[#FBBC04]" />
                  ))}
                </div>
                <p className="mt-2 text-sm leading-relaxed text-[#3A3A3C]">
                  {isRinnaiLocalOffer ? (
                    <>
                      &ldquo;Very happy with the 5kW Rinnai installation. The team was professional.&rdquo;
                      <span className="ml-2 whitespace-nowrap text-xs font-semibold text-[#6E6E73]">— Leilani R., Ashcroft NSW · Google Review</span>
                    </>
                  ) : (
                    <>
                      &ldquo;They were professional from the initial quote through to installation... The workmanship was clean, efficient and we couldn&apos;t be happier.&rdquo;
                      <span className="ml-2 whitespace-nowrap text-xs font-semibold text-[#6E6E73]">— {FEATURED_REVIEW.name}, Google Review</span>
                    </>
                  )}
                </p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row lg:flex-col xl:flex-row">
                <a href="#book" className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0B0B0B] border border-[#C8A46A]/60 px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-white transition-all hover:border-[#C8A46A]">Book Installation <ArrowUpRight className="h-4 w-4" /></a>
                <a href={PHONE_TEL} className="inline-flex items-center justify-center gap-2 rounded-md border border-[#0B0B0B]/15 bg-white px-5 py-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#0B0B0B]">Call Now <Phone className="h-4 w-4" /></a>
              </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 border-t border-[#E8E6E1] pt-4 text-xs font-semibold text-[#4E4E52]">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C8A46A]" /> Fully Licensed &amp; Insured</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> SplitsPro Workmanship Guarantee</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Minimum 5-Year Manufacturer Warranty</span>
              <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Extras confirmed before work starts</span>
            </div>
          </div>
        </section>
        </>
      )}

      {isRinnaiLocalOffer && (
        <>
          <section className="bg-white pb-12 pt-5 sm:py-16" data-testid="local-offer-story">
            <div className="sp-container">
              <div className="mx-auto max-w-5xl">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#C8A46A]">A hassle-free way to install split systems</p>
                <h2 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-[1.06] tracking-tight text-[#0B0B0B] sm:text-5xl lg:text-6xl">
                  No chasing an aircon installer, then an electrician, only to find out the advertised price did not include the bits your home actually needs.
                </h2>
                <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#55555A]">
                  Our standard installed price includes the unit, labour, up to 3 metres of pipework, a standard wall bracket or suitable floor placement, an isolation switch and up to 20 metres of electrical connection if required.
                </p>

                <p className="mt-10 max-w-4xl font-serif text-3xl leading-tight text-[#0B0B0B] sm:text-4xl">
                  Installations within 2 days. A standard install takes just a few hours. Get it sorted now — and beat the summer rush.
                </p>

                <div className="mt-9 flex flex-wrap gap-x-8 gap-y-4 border-y border-[#E5E5EA] py-6 text-sm font-semibold text-[#3A3A3C]">
                  <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#C8A46A]" /> Installations within 2 days</span>
                  <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Up to 20m power circuit if required</span>
                  <span className="flex items-center gap-2"><Zap className="h-4 w-4 text-[#C8A46A]" /> Isolation switch included</span>
                  <span className="flex items-center gap-2"><MoveHorizontal className="h-4 w-4 text-[#C8A46A]" /> Up to 3m pipework</span>
                  <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#C8A46A]" /> Installation guarantee</span>
                  <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Manufacturer warranty</span>
                </div>

                <div className="mt-12 grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
                  <div>
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#C8A46A]">Comfort without the compromise</p>
                    <h3 className="mt-3 font-serif text-3xl font-medium leading-tight text-[#0B0B0B] sm:text-4xl">
                      Premium units — not stripped-back bargain boxes.
                    </h3>
                    <p className="mt-5 text-base leading-relaxed text-[#5F5F63]">
                      Depending on the model, you get Wi-Fi control, inverter power-saving operation, dry / humidity-control modes, quiet operation and smarter airflow — features designed to make the system easier and cheaper to live with, not just cheaper to buy.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-6 border-l-0 border-[#E5E5EA] lg:border-l lg:pl-10">
                    <div><Wifi className="h-5 w-5 text-[#C8A46A]" /><p className="mt-2 font-semibold text-[#0B0B0B]">Smart Wi-Fi</p><p className="mt-1 text-xs leading-relaxed text-[#6E6E73]">Control supported models from your phone.</p></div>
                    <div><Leaf className="h-5 w-5 text-[#C8A46A]" /><p className="mt-2 font-semibold text-[#0B0B0B]">Power saving</p><p className="mt-1 text-xs leading-relaxed text-[#6E6E73]">Inverter operation adjusts output instead of running flat-out.</p></div>
                    <div><Droplets className="h-5 w-5 text-[#C8A46A]" /><p className="mt-2 font-semibold text-[#0B0B0B]">Dry mode</p><p className="mt-1 text-xs leading-relaxed text-[#6E6E73]">Helps manage sticky summer humidity.</p></div>
                    <div><Fan className="h-5 w-5 text-[#C8A46A]" /><p className="mt-2 font-semibold text-[#0B0B0B]">Quiet airflow</p><p className="mt-1 text-xs leading-relaxed text-[#6E6E73]">Comfort without turning the room into a wind tunnel.</p></div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-[#F7F5F1] py-12" data-testid="installation-eligibility"><div className="sp-container max-w-5xl">
            <p className="text-xs font-bold uppercase tracking-widest text-[#8F6A34]">Stage 1 of 3 · Installation check</p>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl">Is your installation standard?</h2>
            <p className="mt-4 text-[#55555A]">Single-storey, back-to-back installation, with up to 3 metres of pipework and straightforward access? A dedicated electrical circuit up to 20 metres is included where required under standard terms.</p>
            <div className="mt-7 flex flex-wrap gap-4">
              <button type="button" onClick={() => {setOfferStage(2);window.setTimeout(() => document.getElementById("local-offer-trust")?.scrollIntoView({behavior:"smooth"}),80);}} className="rounded-lg bg-[#0B0B0B] px-7 py-4 font-bold text-white">Yes — standard installation →</button>
              <a href="/contact?service=split-system-installation" className="rounded-lg border border-[#0B0B0B] px-7 py-4 font-bold text-[#0B0B0B]">No / not sure — request a quote</a>
            </div></div></section>
          {offerStage >= 2 && <>
          <div id="local-offer-sticky-trigger" className="h-px" aria-hidden="true" />
          <section id="local-offer-trust" className="scroll-mt-24 bg-[#F7F5F1] py-10 sm:py-16" data-testid="local-offer-proof">
            <div className="sp-container">
              <div className="mx-auto max-w-5xl">
                <div className="relative overflow-hidden rounded-[30px] bg-[#FCFBF8] px-5 py-7 shadow-[0_22px_70px_rgba(11,11,11,0.07)] sm:px-8 sm:py-10 lg:px-10">
                  <div className="relative z-10 pr-0 sm:pr-36 lg:pr-44">
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#6E6E73]">Real reviews · Real installs · Real accountability</p>
                    <h2 className="mt-3 max-w-4xl font-serif text-4xl font-medium leading-[1.04] tracking-tight text-[#0B0B0B] sm:text-5xl lg:text-6xl">
                      Beat the summer rush with a team you can trust.
                    </h2>
                    <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#55555A] sm:text-lg">
                      Fast is only useful when the job is done properly. You&apos;re trusting someone to drill through your home, run electrical work, mount the system and commission it. We make that easy — and we stand behind what happens after the install, not just the sale.
                    </p>
                  </div>

                  <div className="mt-5 flex justify-end sm:absolute sm:right-5 sm:top-6 sm:mt-0 lg:right-8 lg:top-8">
                    <img
                      src={SOCIAL_PROOF_DOG_IMAGE}
                      alt="Beat the summer rush with a team you can trust"
                      loading="lazy"
                      data-no-fallback="true"
                      className="block h-auto w-[190px] object-contain drop-shadow-[0_16px_26px_rgba(11,11,11,0.14)] sm:w-[215px]"
                    />
                  </div>

                  <div className="mt-7 grid gap-7 lg:grid-cols-[1fr_1.08fr] lg:gap-10">
                    <div className="rounded-[24px] bg-white p-5 shadow-[0_14px_36px_rgba(11,11,11,0.06)] sm:p-6">
                      <GoogleRating />
                      <div className="mt-5 flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-5 w-5 fill-[#FBBC04] text-[#FBBC04]" />)}
                      </div>
                      <blockquote className="mt-4 max-w-xl font-serif text-2xl leading-snug text-[#2E2E31]">
                        &ldquo;Very happy with the 5kW Rinnai installation. The team was professional.&rdquo;
                      </blockquote>
                      <p className="mt-3 text-sm font-semibold text-[#6E6E73]">— Leilani R., Ashcroft NSW · Google Review</p>
                      <p className="mt-6 max-w-xl text-sm leading-relaxed text-[#5F5F63]">
                        Reviews tell you how we treated customers before you. Our licences, warranties and installation guarantee tell you what you can expect after we leave.
                      </p>
                    </div>

                    <div className="grid gap-0 rounded-[24px] border border-[#E5E1D9] bg-white px-5 sm:px-6">
                      <div className="border-b border-[#E7E3DC] py-5">
                        <div className="flex items-start gap-3">
                          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                          <div>
                            <h3 className="font-semibold text-[#0B0B0B]">Fully licensed &amp; insured</h3>
                            <p className="mt-1 text-sm leading-relaxed text-[#6E6E73]">The electrical and air-conditioning work is handled as one organised installation — not something you need to coordinate yourself.</p>
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-[#E7E3DC] py-5">
                        <div className="flex items-start gap-3">
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                          <div>
                            <h3 className="font-semibold text-[#0B0B0B]">Installation guarantee</h3>
                            <p className="mt-1 text-sm leading-relaxed text-[#6E6E73]">We stand behind the workmanship as well as the unit, so an installation issue does not become your problem to chase.</p>
                          </div>
                        </div>
                      </div>
                      <div className="border-b border-[#E7E3DC] py-5">
                        <div className="flex items-start gap-3">
                          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                          <div>
                            <h3 className="font-semibold text-[#0B0B0B]">Manufacturer-backed warranty</h3>
                            <p className="mt-1 text-sm leading-relaxed text-[#6E6E73]">7-year manufacturer warranty on the Rinnai specials and 5-year manufacturer warranty on Daikin Cora.</p>
                          </div>
                        </div>
                      </div>
                      <div className="py-5">
                        <div className="flex items-start gap-3">
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                          <div>
                            <h3 className="font-semibold text-[#0B0B0B]">Clear standard inclusions</h3>
                            <p className="mt-1 text-sm leading-relaxed text-[#6E6E73]">The advertised standard-install price includes labour, commissioning, up to 3 metres of pipework, a standard wall bracket or suitable floor placement, an isolation switch and up to 20 metres of electrical connection if required.</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                              <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#7B5A28]">Wall bracket included</span>
                              <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#7B5A28]">Up to 20m electrical connection*</span>
                              <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#7B5A28]">Isolation switch included</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-9 border-t border-[#E3DED6] pt-8">
                    <p className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#6E6E73]">See the standard for yourself</p>
                    <h3 className="mt-2 font-serif text-3xl font-medium text-[#0B0B0B] sm:text-4xl">Clean work. Properly finished. Built to stay that way.</h3>
                    <div className="mt-6 grid grid-cols-2 gap-3 sm:gap-4">
                      <figure className="m-0 overflow-hidden rounded-[20px]">
                        <img
                          src={brand.installEditorial?.primary?.src}
                          alt={brand.installEditorial?.primary?.alt || "SplitsPro Rinnai outdoor installation"}
                          loading="lazy"
                          data-no-fallback="true"
                          className="aspect-[4/3] w-full object-cover"
                        />
                      </figure>
                      <figure className="m-0 overflow-hidden rounded-[20px]">
                        <img
                          src={brand.installEditorial?.secondary?.src}
                          alt={brand.installEditorial?.secondary?.alt || "SplitsPro Rinnai indoor installation"}
                          loading="lazy"
                          data-no-fallback="true"
                          className="aspect-[4/3] w-full object-cover"
                        />
                      </figure>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white py-14 sm:py-20" data-testid="local-offer-market-check">
            <div className="sp-container">
              <div className="mx-auto max-w-5xl">
                <p className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#C8A46A]">Then we checked the market</p>
                <h2 className="mt-4 max-w-4xl font-serif text-4xl font-medium leading-tight tracking-tight text-[#0B0B0B] sm:text-5xl">
                  Same brands. Same size class. Very different numbers.
                </h2>
                <p className="mt-5 max-w-3xl text-base leading-relaxed text-[#5F5F63]">
                  These are current advertised installed examples we found online. Different installers use different models, inclusions and site conditions — so this is a price check, not a claim that every quote is identical. With SplitsPro, the value is in what the installed price already includes.
                </p>

                <div className="mt-10 grid gap-10 border-y border-[#E5E5EA] py-8 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#6E6E73]">Rinnai 7kW installed</p>
                    <p className="mt-3 text-lg text-[#6E6E73]">Other advertised example</p>
                    <p className="font-serif text-4xl text-[#6E6E73] line-through">$2,799</p>
                    <p className="mt-5 text-sm font-bold uppercase tracking-[0.14em] text-[#8F6A34]">SplitsPro</p>
                    <p className="font-serif text-5xl text-[#0B0B0B]">$2,600</p>
                    <p className="mt-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8F6A34]">Your SplitsPro price already includes</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Up to 20m electrical connection*</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Isolation switch</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Wall bracket</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Up to 3m pipework</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Labour + commissioning</span>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-bold uppercase tracking-[0.14em] text-[#6E6E73]">Daikin Cora 7.1kW installed</p>
                    <p className="mt-3 text-lg text-[#6E6E73]">Other advertised example</p>
                    <p className="font-serif text-4xl text-[#6E6E73] line-through">$3,411.94</p>
                    <p className="mt-5 text-sm font-bold uppercase tracking-[0.14em] text-[#8F6A34]">SplitsPro</p>
                    <p className="font-serif text-5xl text-[#0B0B0B]">$3,000</p>
                    <p className="mt-3 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#8F6A34]">Your SplitsPro price already includes</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Up to 20m electrical connection*</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Isolation switch</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Wall bracket</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Up to 3m pipework</span>
                      <span className="rounded-full bg-[#F3E9D2] px-3 py-1.5 text-[10px] font-bold text-[#7B5A28]">Labour + commissioning</span>
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-[10px] leading-relaxed text-[#8A8A8E]">
                  Price check: Ozcon Air listed a Rinnai 7kW supplied &amp; installed at $2,799; Hewitt Trade Services listed Daikin Cora 7.1kW supplied &amp; installed at $3,411.94. Competitor inclusions and installation conditions vary. SplitsPro standard-install pricing includes the wall bracket, isolation switch, up to 20m electrical connection if required, up to 3m pipework, labour and commissioning. Checked 29 Sep 2026.
                </p>

                <p className="mt-12 font-serif text-3xl text-[#0B0B0B] sm:text-4xl">Now pick the size that suits your home.</p>
              </div>
            </div>
          </section>
          <div className="bg-[#F7F5F1] py-10"><div className="sp-container">
            <p className="text-xs font-bold uppercase tracking-widest text-[#8F6A34]">Stage 2 of 3 · Premium systems &amp; trust</p>
            <button type="button" onClick={() => {setOfferStage(3);window.setTimeout(() => document.getElementById("installed-prices")?.scrollIntoView({behavior:"smooth"}),80);}} className="mt-4 rounded-lg bg-[#0B0B0B] px-7 py-4 font-bold text-white">View offers and book →</button>
          </div></div>
          </>}
        </>
      )}


      {isRinnaiLocalOffer && offerStage >= 3 && (
        <section id="installed-prices" className="scroll-mt-24 bg-white py-10 sm:py-16" data-testid="current-installed-deals">
          <div className="sp-container">
            <div className="mx-auto max-w-6xl">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#8F6A34]">SplitsPro · Current installed deals</p>
              <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
                <div>
                  <h2 className="font-serif text-3xl leading-tight text-[#0B0B0B] sm:text-5xl">Choose your air conditioner and installation day.</h2>
                  <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[#606064]">Tap the large Rinnai or Daikin card, select your system size, and pick a live installation time. Then choose a 10% booking payment, full payment or eligible pay-later options.</p>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <a href="#local-deal-booking" className="rounded-md bg-[#0B0B0B] px-5 py-3 text-xs font-extrabold uppercase tracking-[0.08em] text-white">View installed prices ↓</a>
                  <a href="#local-deal-booking" className="text-xs font-bold text-[#08778D] underline underline-offset-4">Ask us about payment options ↓</a>
                </div>
              </div>
              <div className="mt-6" data-testid="installation-offer-pay-later-reminder">
                <PayLaterReminder kind="equipment" equipmentHref="/split-systems/rinnai-local-offer#local-deal-booking" />
              </div>
              <div id="local-deal-booking" className="mt-9 rounded-xl border border-[#E5E1D9] bg-white p-6">
                <h3 className="font-serif text-2xl">See installed prices, then call us</h3>
                <p className="mt-2 text-sm text-[#555]">No online date selection, booking or deposit. Compare system sizes and prices, then arrange installation over the phone.</p>
                <a href="tel:0414698435" className="mt-4 inline-flex rounded-md bg-[#0B0B0B] px-6 py-3 font-bold text-white">Call to Book</a>
              </div>
              <div id="pay-later-deals" className="mt-8 border-l-4 border-[#087D94] bg-[#EEF8FA] p-5 sm:p-7" data-testid="payment-options">
                <h3 className="font-serif text-2xl text-[#0B0B0B]">Buy now. Pay later. Stay cool.</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#374B55]">Available Stripe checkout methods may include <strong>Afterpay, Klarna and Zip</strong>, plus cards. <strong>PayPal Pay in 4</strong> uses a separate PayPal checkout and is only shown for eligible air conditioners where the installed deal is $2,000 or less and the equipment price qualifies.</p>
                <p className="mt-3 text-xs leading-relaxed text-[#374B55]">First choose your unit, date and morning or afternoon appointment above. On the next page choose a booking payment or the full installed price through Stripe, with pay-later methods where eligible. Your date is confirmed only after payment is verified.</p>
                <a href="#local-deal-booking" className="mt-3 inline-block text-sm font-bold text-[#00556D] underline underline-offset-4">Choose unit, date and time to view payment options →</a>
              </div>
              <p className="mt-5 text-xs leading-relaxed text-[#525960]"><strong>Our No More to Pay installation model</strong> includes supply, installation labour and commissioning, up to 3m of refrigeration pipework, a suitable wall bracket or floor placement, an isolator, and up to 20m of electrical cable and a dedicated circuit from the switchboard where required. Non-standard extras are quoted before work proceeds.</p>
              <p className="mt-2 text-[11px] leading-relaxed text-[#777]">*Subject to appointment availability, service area and qualifying standard installation terms. Finance availability and individual eligibility are checked by Stripe or PayPal at checkout. The installed total is not the equipment-only finance amount.</p>
            </div>
          </div>
        </section>
      )}

      {!isRinnaiLocalOffer && <BrandSelectionBanner currentSlug={brand.slug} />}

      {/* Brand intro */}
      {!isRinnaiLocalOffer && (brand.installEditorial ? (
        <DaikinIntroEditorial brand={brand} jumpToRange={jumpToRange} />
      ) : (
        <section className="bg-white py-14 sm:py-20" data-testid="brand-intro">
          <div className="sp-container">
            <Link to="/split-systems" data-testid="brand-back" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#C8A46A] link-line">
              <ArrowLeft className="h-4 w-4" /> Split System Air Conditioning
            </Link>
            <div className="mt-5 max-w-3xl">
              <p className="leading-relaxed text-[#6E6E73]">
                {isRinnaiLocalOffer
                  ? "Pick the brand, pick the size, see the supplied-and-installed price. Rinnai and Daikin Cora are both available across Sydney, the Central Coast and Wollongong."
                  : brand.body}
              </p>
            </div>
            {displayRanges.length > 1 && (
              <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="range-tabs">
                {displayRanges.map((r) => (
                  <button key={r.slug} onClick={() => jumpToRange(r.slug)} data-testid={`range-tab-${r.slug}`}
                    className="rounded-full border border-[#0B0B0B]/15 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#0B0B0B] transition-all hover:border-[#C8A46A] hover:text-[#C8A46A]">
                    {r.tabLabel || r.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>
      ))}

            {/* Ranges + pricing tables */}
      {!isRinnaiLocalOffer && displayRanges.map((range, ri) => (
        <section
          key={range.slug}
          id={`range-${range.slug}`}
          data-testid={`range-${range.slug}`}
          className={`scroll-mt-24 py-16 sm:py-20 ${ri % 2 === 0 ? "bg-[#F5F5F7]" : "bg-white"}`}
        >
          <div className="sp-container">
          <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] lg:items-center lg:gap-12">
            <div className="max-w-3xl">
              <div className="flex flex-wrap items-center gap-3">
                <span className="overline text-[#C8A46A]">{range.manufacturer || brand.brand}</span>
                {isRinnaiLocalOffer && (
                  <span className="rounded-full border border-[#C8A46A]/50 bg-[#F3E9D2] px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#7B5A28]">Limited spots</span>
                )}
              </div>
              <h2 className="mt-3 font-serif text-3xl font-medium leading-tight tracking-tight text-[#0B0B0B] md:text-4xl text-balance">
                {range.displayName || `${brand.brand} ${range.name}`}
              </h2>
              <p className="mt-4 leading-relaxed text-[#6E6E73]">{range.blurb}</p>

              {brand.slug === "daikin" && DAIKIN_COMPACT_FEATURES[range.slug] ? (
                <div className="mt-6" data-testid={`feature-details-${range.slug}`}>
                  <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-[#008CCF]">Daikin key features</p>
                  <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3">
                    {DAIKIN_COMPACT_FEATURES[range.slug].map((feature) => {
                      const Icon = DAIKIN_ICON_MAP[feature.icon] || Sparkles;
                      return (
                        <div key={feature.title} className={`flex min-w-0 items-start gap-2.5 ${feature.highlight ? "col-span-2 rounded-xl border-2 border-[#C8A46A] bg-[#FFF8E8] p-4 sm:col-span-3" : ""}`}>
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#009FE3]/10 text-[#008CCF]">
                            <Icon className="h-4 w-4" strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0">
                            <h3 className={feature.highlight ? "text-2xl font-black leading-tight text-[#0B0B0B] sm:text-3xl" : "text-[12px] font-bold leading-4 text-[#0B0B0B]"}>{feature.title}</h3>
                            <p className="mt-0.5 text-[11px] leading-[1.4] text-[#6E6E73]">{feature.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {(range.slug === "alira-x" || range.slug === "zena") && (
                    <p className="mt-4 max-w-2xl text-[10px] leading-[1.45] text-[#8A8A8E]">
                      {DAIKIN_STREAMER_FOOTNOTE}
                    </p>
                  )}
                </div>
              ) : (
                <div className="mt-6" data-testid={`feature-details-${range.slug}`}>
                  <p className="mb-4 text-[10px] font-bold uppercase tracking-[0.2em] text-[#6E6E73]">Key features</p>
                  <div className="grid grid-cols-2 gap-x-5 gap-y-4 sm:grid-cols-3">
                    {(NON_DAIKIN_FEATURES[range.slug] || []).map((feature) => {
                      const Icon = feature.fallback || Sparkles;
                      return (
                        <div key={feature.title} className="flex min-w-0 items-start gap-2.5">
                          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#E5E5EA] bg-white text-[#C8A46A]">
                            {feature.icon && (
                              <img
                                src={feature.icon}
                                alt=""
                                aria-hidden="true"
                                loading="lazy"
                                className="h-5 w-5 object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  e.currentTarget.nextElementSibling?.classList.remove("hidden");
                                }}
                              />
                            )}
                            <Icon className={`h-4 w-4 ${feature.icon ? "hidden" : ""}`} strokeWidth={1.9} />
                          </span>
                          <div className="min-w-0">
                            <h3 className="text-[12px] font-bold leading-4 text-[#0B0B0B]">{feature.title}</h3>
                            <p className="mt-0.5 text-[11px] leading-[1.4] text-[#6E6E73]">{feature.desc}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {range.note && (
                    <p className="mt-4 max-w-2xl text-[10px] leading-[1.45] text-[#8A8A8E]">{range.note}</p>
                  )}
                </div>
              )}
            </div>
            {range.image && (
              <div
                className={`${range.slug === "zena" ? "w-full lg:col-span-2" : "mx-auto w-full max-w-2xl lg:mx-0 lg:justify-self-end"}`}
                data-testid={`range-image-${range.slug}`}
              >
                {range.slug === "zena" && range.gallery?.length >= 2 ? (
                  <div className="mt-2 grid gap-8 sm:grid-cols-2 sm:gap-10" data-testid="zena-two-finish-showcase">
                    {range.gallery.slice(0, 2).map((item, idx) => (
                      <figure key={`${range.slug}-large-${idx}`} className="m-0 flex min-h-[260px] flex-col items-center justify-center sm:min-h-[320px] lg:min-h-[360px]">
                        <img
                          src={item.src}
                          alt={item.alt}
                          loading="eager"
                          data-no-fallback="true"
                          onError={(e) => { e.currentTarget.style.display = "none"; }}
                          className="zena-showcase-image block w-full object-contain"
                        />
                        <figcaption className="mt-3 text-xs font-semibold uppercase tracking-[0.14em] text-[#6E6E73]">{idx === 0 ? "Black Wood" : "White Hair Line"}</figcaption>
                      </figure>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="product-media-seamless flex min-h-[250px] items-center justify-center px-1 py-4 sm:min-h-[320px] lg:min-h-[380px]">
                      <img
                        src={OFFICIAL_PRODUCT_IMAGES[range.slug] || range.image}
                        alt={`${brand.brand} ${range.name} split system air conditioner`}
                        loading="lazy"
                        data-no-fallback="true"
                        onError={(e) => {
                          if (OFFICIAL_PRODUCT_IMAGES[range.slug] && e.currentTarget.dataset.officialFallback !== "true") {
                            e.currentTarget.dataset.officialFallback = "true";
                            e.currentTarget.src = range.image;
                          } else {
                            e.currentTarget.style.display = "none";
                          }
                        }}
                        className="product-unit-image block max-h-[340px] w-full object-contain sm:max-h-[410px] lg:max-h-[470px]"
                      />
                    </div>
                    {range.gallery?.length > 1 && (
                      <div className="mt-1 flex flex-wrap items-center justify-center gap-4 sm:gap-6" data-testid={`range-gallery-${range.slug}`}>
                        {range.gallery.slice(0, 3).map((item, idx) => (
                          <div key={`${range.slug}-${idx}`} className="product-thumb-seamless flex h-24 w-[44%] max-w-[190px] items-center justify-center sm:h-28 sm:w-[30%]">
                            <img
                              src={item.src}
                              alt={item.alt}
                              loading="lazy"
                              data-no-fallback="true"
                              onError={(e) => {
                          if (OFFICIAL_PRODUCT_IMAGES[range.slug] && e.currentTarget.dataset.officialFallback !== "true") {
                            e.currentTarget.dataset.officialFallback = "true";
                            e.currentTarget.src = range.image;
                          } else {
                            e.currentTarget.style.display = "none";
                          }
                        }}
                              className="product-unit-image h-full w-full object-contain"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 border-y border-[#E5E5EA] py-3 text-xs text-[#5F5F63]" data-testid={`range-proof-${range.slug}`}>
              {ri % 3 === 0 && (
                <>
                  <span className="flex items-center gap-2 font-semibold"><ShieldCheck className="h-4 w-4 text-[#C8A46A]" /> Licensed &amp; insured installation</span>
                  <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Workmanship guarantee included</span>
                </>
              )}
              {ri % 3 === 1 && (
                <>
                  <span className="flex items-center gap-2 font-semibold"><Star className="h-4 w-4 fill-[#FBBC04] text-[#FBBC04]" /> 5-star customer feedback</span>
                  <span>&ldquo;Clean, efficient workmanship and a high standard of installation.&rdquo; — Google review</span>
                </>
              )}
              {ri % 3 === 2 && (
                <>
                  <span className="flex items-center gap-2 font-semibold"><Check className="h-4 w-4 text-[#C8A46A]" /> Minimum 5-year manufacturer warranty</span>
                  <span className="flex items-center gap-2"><Check className="h-4 w-4 text-[#C8A46A]" /> Any extras confirmed before work starts</span>
                </>
              )}
            </div>

            <div className="mt-6"><PayLaterReminder kind="equipment" /></div>
            <p className="mt-7 text-xs font-semibold uppercase tracking-[0.22em] text-[#6E6E73]">Supplied &amp; Installed</p>
            <div className="mt-3 overflow-hidden rounded-2xl border border-[#E5E5EA] bg-white soft-shadow-sm">
              {range.prices.map((row, i) => (
                <div
                  key={row.kw}
                  data-testid={`price-row-${range.slug}-${row.kw.replace(/[^0-9a-z]/gi, "")}`}
                  className={`grid gap-3 px-6 py-5 sm:grid-cols-[1fr_1fr_auto] sm:items-center sm:gap-8 ${i > 0 ? "border-t border-[#E5E5EA]" : ""}`}
                >
                  <span>
                    <span className="block font-serif text-xl text-[#0B0B0B] sm:text-2xl">{row.kw}</span>
                    {row.model && <span className="mt-1 block text-xs font-medium text-[#6E6E73]">Model {row.model}</span>}
                    {isRinnaiLocalOffer && (
                      <span className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#C8A46A]/50 bg-[#FFF8E8] px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-[0.1em] text-[#7B5A28]">
                        <Zap className="h-3.5 w-3.5" /> Installed within 2 days — guaranteed
                      </span>
                    )}
                  </span>
                  <span className="text-[#0B0B0B]">
                    {isRinnaiLocalOffer ? (
                      row.localOfferPrice ? (
                        <span className="flex flex-col items-start">
                          <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#8A8A8E]">Supplied &amp; installed</span>
                          <span className="mt-1 font-serif text-2xl text-[#0B0B0B] sm:text-3xl">{row.localOfferPrice}</span>
                          <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#B58C4E]">No more to pay*</span>
                        </span>
                      ) : (
                        <span className="flex flex-col items-start">
                          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-[#8A8A8E] line-through">Was {row.price}</span>
                          <span className="mt-1 font-serif text-2xl text-[#0B0B0B] sm:text-3xl">{getRinnaiLocalSalePrice(row.price)}</span>
                          <span className="mt-1 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#B58C4E]">{RINNAI_LOCAL_DISCOUNT_LABEL} · Limited spots</span>
                        </span>
                      )
                    ) : (
                      <span className="font-serif text-xl sm:text-2xl">{row.price}</span>
                    )}
                  </span>
                  <button
                    onClick={() => handleBook(range, row)}
                    data-testid={`book-btn-${range.slug}-${row.kw.replace(/[^0-9a-z]/gi, "")}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#0B0B0B] border border-[#C8A46A]/60 px-6 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-[#F8F7F5] transition-all hover:border-[#C8A46A] hover:text-[#E4CFA6] hover:-translate-y-[2px]"
                  >
                    {isRinnaiLocalOffer ? "Book This Offer" : "Book Installation"} <ArrowUpRight className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs leading-relaxed text-[#6E6E73]" data-testid={`disclaimer-${range.slug}`}>
              {isRinnaiLocalOffer
                ? "*No more to pay applies to qualifying standard installations including up to 3 metres of refrigeration pipework, a standard wall bracket or suitable floor placement, an isolation switch and up to 20 metres of electrical connection if required. Switchboard upgrades, pipe runs over 3 metres, difficult access, asbestos-related work and other non-standard requirements are quoted before proceeding."
                : "Standard back-to-back installation pricing. Additional pipework, electrical work, brackets or non-standard access may cost extra. Any additional costs are confirmed before work proceeds."}
            </p>
          </div>
        </section>
      ))}



      {/* Booking form — pre-filled with selection */}
      {!isRinnaiLocalOffer && (
      <section id="book" className="scroll-mt-24 bg-[#0B0B0B] py-24 sm:py-32" data-testid="brand-book-section">
        <div className="sp-container grid gap-14 lg:grid-cols-2 lg:gap-20">
          <div>
            <span className="overline text-[#C8A46A]">Book Installation</span>
            <h2 className="mt-5 font-serif text-4xl font-medium leading-tight tracking-tight text-white md:text-5xl text-balance">
              {selected
                ? `Book your ${selected.displayName} ${selected.kw}`
                : isRinnaiLocalOffer
                  ? "Book your split system installation"
                  : `Book your ${brand.brand} installation`}
            </h2>
            {selected && (
              <p className="mt-5 text-lg text-[#C8A46A]" data-testid="brand-selected-summary">
                {selected.price} · Supplied &amp; Installed{isRinnaiLocalOffer ? " · Installed within 2 days" : ""}
              </p>
            )}
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/75">
              Busy day? No problem. A standard split-system install normally takes just a few hours, and you can usually keep using the rest of your home while we work.
            </p>
            <div className="mt-8 grid max-w-lg gap-5" data-testid="installation-reassurance">
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                <div><p className="font-semibold text-white">Small work area</p><p className="mt-1 text-sm leading-relaxed text-white/60">We only need access around the indoor and outdoor unit positions, not your whole home.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                <div><p className="font-semibold text-white">Minimal disruption</p><p className="mt-1 text-sm leading-relaxed text-white/60">You can normally carry on using the rest of the house while the installation is underway.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                <div><p className="font-semibold text-white">Power stays on</p><p className="mt-1 text-sm leading-relaxed text-white/60">Only the necessary circuit is isolated for the electrical connection, usually for about 10–15 minutes on a standard job.</p></div>
              </div>
              <div className="flex items-start gap-3">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#C8A46A]" />
                <div><p className="font-semibold text-white">Clean when we leave</p><p className="mt-1 text-sm leading-relaxed text-white/60">We manage the mess as we work, pack everything up and leave the installation area neat and tidy.</p></div>
              </div>
            </div>
          </div>
          <Reveal delay={0.1}>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-8 backdrop-blur-md sm:p-10" data-testid="brand-quote-card">
              <QuoteForm
                key={formKey}
                onDark
                defaultService="Split System Installation"
                defaultMessage={selectionMessage}
                submitLabel={submitLabel}
                compact
                hideMessage
              />
              <ul className="mt-6 flex flex-wrap gap-x-4 gap-y-2 border-t border-white/10 pt-5">
                {[
                  "Minimum 5-Year Manufacturer Warranty",
                  "SplitsPro Workmanship Guarantee",
                  isRinnaiLocalOffer ? "Installed special pricing shown above" : "Standard installation pricing shown above",
                  "Any extras confirmed before work starts",
                ].map((t) => (
                  <li key={t} className="flex items-center gap-1.5 text-xs font-medium text-white/70">
                    <Check className="h-3.5 w-3.5 text-[#C8A46A]" strokeWidth={2.5} /> {t}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>
      )}

      {/* Sibling brand nav — compact */}
      <section className="bg-white py-14">
        <div className="sp-container flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-2" data-testid="sibling-brands">
            <span className="text-xs font-semibold uppercase tracking-[0.16em] text-[#6E6E73]">Other brands:</span>
            {SPLIT_BRANDS.filter((b) => b.slug !== brand.slug).map((b) => (
              <Link key={b.slug} to={`/split-systems/${b.slug}`} data-testid={`brand-link-${b.slug}`}
                className="rounded-full border border-[#0B0B0B]/15 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#0B0B0B] transition-all hover:border-[#C8A46A] hover:text-[#C8A46A]">
                {b.brand}
              </Link>
            ))}
          </div>
          <Link to="/split-systems" data-testid="brand-back-main" className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-[#C8A46A] link-line">
            <ArrowLeft className="h-4 w-4" /> All split systems
          </Link>
        </div>
      </section>

      <section className="bg-[#0B0B0B] py-14" data-testid="brand-final-book-call">
        <div className="sp-container flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C8A46A]">Ready to install?</p>
            <p className="mt-2 font-serif text-2xl text-white">Book your {brand.brand} installation or call us now.</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a href={isRinnaiLocalOffer ? "#installed-prices" : "#book"} className="inline-flex items-center justify-center gap-2 rounded-md bg-[#C8A46A] px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-white">Book Installation <ArrowUpRight className="h-4 w-4" /></a>
            <a href={PHONE_TEL} className="inline-flex items-center justify-center gap-2 rounded-md border border-white/30 px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.16em] text-white">Call Now <Phone className="h-4 w-4" /></a>
          </div>
        </div>
      </section>
      {isRinnaiLocalOffer && offerStage >= 3 && <FloatingBookingShortcut />}
      {isRinnaiLocalOffer && <ExitQuoteCapture selectedUnit={selected} />}
    </>
  );
};

export default BrandPage;
